import { task, all } from 'ember-concurrency';
import Controller from '@ember/controller';
import { service } from '@ember/service';
import { action } from '@ember/object';
import { anyInvalid } from 'eflex/util/getter-helpers';
import { tracked } from '@glimmer/tracking';
import { waitFor } from '@ember/test-waiters';
import { userRolesValues } from 'eflex/constants/user-roles';

export default class SettingsUsersController extends Controller {
  @tracked showUserConfigPanel = false;
  @tracked showChangePasswordModal = false;
  @tracked showChangeBadgeIdModal = false;
  @tracked password;
  @tracked passwordConfirmation;
  @tracked badgeId;
  @tracked badgeIdConfirmation;
  @tracked selectedUser;
  @tracked users = [];

  @service validationErrorNotifier;
  @service notifier;
  @service currentUser;
  @service store;
  @service intl;

  get isInvalid() {
    return anyInvalid(this.users);
  }

  get isDirty() {
    return this.users.some(item => item.isDirty);
  }

  get userRoleOptions() {
    return userRolesValues;
  }

  get changePasswordEnabled() {
    if (this.isDirty) {
      return false;
    }

    return (this.selectedUser && this.currentUser.user.isAdmin) || !this.currentUser.user?.isAdmin;
  }

  get formattedUserPermissions() {
    return this.currentUser.user.permissions
      ?.map(permission => this.intl.t(`permissions.${permission}`))
      .join(', ');
  }

  save = task(waitFor(async () => {
    const dirtyUsers = this.users.filter(item => item.isDirty);

    if (dirtyUsers.some(item => item.isInvalid)) {
      this.validationErrorNotifier.sendErrors(dirtyUsers);
      return;
    }

    const showNotice = this.selectedUser?.changedAttributes().timeoutLength != null;

    await all(dirtyUsers.map(dirtyUser => dirtyUser.save()));

    if (showNotice) {
      this.notifier.sendWarning('settings.users.updatedTimeout');
    }
  }));

  removeBadgeId = task(waitFor(async () => {
    this.selectedUser.badgeId = '';
    await this.selectedUser.save();

    this.notifier.sendSuccess('logs.badgeIdRemoved');
    this.selectedUser.badgeId = null;
    Object.assign(this, {
      showChangeBadgeIdModal: false,
      badgeId: null,
      badgeIdConfirmation: null,
    });
  }));

  changeBadgeId = task(waitFor(async () => {
    if (!this.badgeId || !this.badgeIdConfirmation) {
      this.notifier.sendError('logs.badgeIdCannotBeBlank');
      return;
    }

    if (this.badgeId !== this.badgeIdConfirmation) {
      this.notifier.sendError('logs.badgeIdsDoNotMatch');
      return;
    }

    this.selectedUser.badgeId = this.badgeId;

    try {
      await this.selectedUser.save();
    } catch (e) {
      this.notifier.sendError(e);
      this.selectedUser.badgeId = null;
      Object.assign(this, {
        badgeId: null,
        badgeIdConfirmation: null,
      });
      return;
    }

    Object.assign(this, {
      showChangeBadgeIdModal: false,
      badgeId: null,
      badgeIdConfirmation: null,
    });
    this.selectedUser.badgeId = null;

    this.notifier.sendSuccess('logs.badgeIdUpdated');
  }));

  passwordChangeComplete = task(waitFor(async () => {
    if (!this.password || !this.passwordConfirmation) {
      this.notifier.sendError('logs.passwordCannotBeBlank');
      return;
    }

    if (this.password !== this.passwordConfirmation) {
      this.notifier.sendError('logs.passwordsDoNotMatch');
      return;
    }

    this.selectedUser.password = this.password;
    await this.selectedUser.save();

    Object.assign(this, {
      showChangePasswordModal: false,
      password: null,
      passwordConfirmation: null,
    });

    this.notifier.sendSuccess('logs.passwordUpdated');
  }));

  clearPanelContext() {
    Object.assign(this, {
      selectedUser: null,
      password: null,
      passwordConfirmation: null,
      showUserConfigPanel: false,
    });
  }

  @action
  createUser() {
    const user = this.store.createRecord('user', {});

    Object.assign(this, {
      selectedUser: user,
      showUserConfigPanel: true,
    });
  }

  @action
  rollback() {
    this.users.forEach(user => { user.rollbackAttributes(); });

    if (this.selectedUser?.isDeleted) {
      this.selectedUser = this.users[0];
    }

    this.closePanel();
  }

  @action
  openUserConfigPanel(user) {
    Object.assign(this, {
      selectedUser: user,
      showUserConfigPanel: true,
    });
  }

  @action
  closePanel() {
    if (this.selectedUser?.isInvalid) {
      this.validationErrorNotifier.sendErrors(this.selectedUser);
      return;
    }

    this.clearPanelContext();
  }

  @action
  openChangeEncryptedPasswordModal() {
    this.selectedUser ??= this.currentUser.user;
    this.showChangePasswordModal = true;
  }

  @action
  openChangeEncryptedBadgeIdModal() {
    this.selectedUser ??= this.currentUser.user;
    this.showChangeBadgeIdModal = true;
  }

  @action
  cancelPasswordChange() {
    Object.assign(this, {
      showChangePasswordModal: false,
      password: null,
      passwordConfirmation: null,
    });
  }

  @action
  cancelBadgeIdChange() {
    Object.assign(this, {
      showChangeBadgeIdModal: false,
      badgeId: null,
      badgeIdConfirmation: null,
    });
  }

  @action
  deleteUser(user) {
    user.deleteRecord();

    this.clearPanelContext();
  }
}
